// Single source of truth for password requirements, shared by the live
// checklist (<PasswordRequirements />) and the submit guard (useRegister /
// reset). Keeping the rules here means the checkmarks and the «can submit»
// gate can never disagree.
export interface PasswordRule {
  label: string;
  test: (password: string) => boolean;
}

// CORE-515 (QA 2026-07-25) — allowed alphabet: printable ASCII only, i.e. Latin
// letters, digits and the usual specials (0x21–0x7E). Everything else is out:
// Cyrillic (which used to pass — the uppercase rule matched А-ЯЁ), emoji, other
// Unicode, and whitespace. Rationale: a password typed in a non-Latin layout is
// unreproducible on another keyboard/IME, and spaces are invisible typos users
// can never see in a masked field. The range is deliberately permissive about
// specials — no character class inside it is rejected, so password managers and
// generated passwords keep working.
const ALLOWED_PASSWORD_CHARS = /^[\x21-\x7E]+$/;

export const PASSWORD_RULES: PasswordRule[] = [
  { label: 'Минимум 8 символов', test: (p) => p.length >= 8 },
  { label: 'Одна заглавная буква', test: (p) => /[A-Z]/.test(p) },
  { label: 'Одна цифра', test: (p) => /[0-9]/.test(p) },
  { label: 'Только латинские буквы, цифры и символы', test: (p) => ALLOWED_PASSWORD_CHARS.test(p) },
];

export const isPasswordValid = (password: string): boolean =>
  PASSWORD_RULES.every((rule) => rule.test(password));

// CORE-914 — на нативной форме регистрации (и сброса пароля) кнопка гасла при
// расхождении паролей, но причину пользователю не показывали: canSubmit требует
// совпадения, а текст «Пароли не совпадают» жил внутри submit(), который при
// выключенной кнопке не срабатывал. Эта подпись под полем подтверждения делает
// причину видимой. Тон — по гайду Agapella: спокойно, без «Ошибка!».
export const PASSWORD_MISMATCH_MESSAGE = 'Пароли не совпадают';

// Видимую подсказку показываем не сразу: пока пользователь ещё печатает повтор,
// ругаться на каждый символ — хуже молчания. `touched` поднимается на blur поля
// подтверждения (или первой попытке отправки), и только тогда — при непустом и
// расходящемся значении — возвращаем сообщение.
export function confirmPasswordError(
  password: string,
  confirm: string,
  touched: boolean,
): string | null {
  if (!touched || confirm.length === 0) return null;
  return confirm === password ? null : PASSWORD_MISMATCH_MESSAGE;
}
